import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CalendarDays, CheckSquare, StickyNote, Users,
  Plus, X, ChevronLeft, ChevronRight, Check, Search,
  MessageCircle, ArrowLeft, Send,
} from 'lucide-react'
import { useUIStore } from '../../stores/uiStore'
import type { WorkspacePanelType } from '../../stores/uiStore'

// ─── Calendar Panel ───────────────────────────────────────────────────────────

const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

const EVENT_COLORS = ['#5B9FE8', '#E07040', '#6DB87A', '#A855F7', '#F59E0B', '#E85454', '#06B6D4']

interface CalEvent {
  id: string
  title: string
  time: string
  date: string   // YYYY-MM-DD
  color: string
}

function dateKey(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function CalendarPanel() {
  const now = new Date()
  const [viewDate, setViewDate] = useState(() => new Date(now.getFullYear(), now.getMonth(), 1))
  const [selectedDay, setSelectedDay] = useState(now.getDate())
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth())
  const [selectedYear, setSelectedYear] = useState(now.getFullYear())

  // Events persisted in localStorage
  const [events, setEvents] = useState<CalEvent[]>(() => {
    try { return JSON.parse(localStorage.getItem('tmail_events') ?? '[]') } catch { return [] }
  })

  useEffect(() => {
    try { localStorage.setItem('tmail_events', JSON.stringify(events)) } catch { /* ignore */ }
  }, [events])

  // New event form
  const [adding, setAdding] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newTime, setNewTime] = useState('09:00')
  const [newColor, setNewColor] = useState(EVENT_COLORS[0])
  const titleRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (adding) setTimeout(() => titleRef.current?.focus(), 50)
  }, [adding])

  const year = viewDate.getFullYear()
  const month = viewDate.getMonth()
  const monthName = viewDate.toLocaleString('default', { month: 'long' })
  const firstDow = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  const cells: (number | null)[] = []
  for (let i = 0; i < firstDow; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  const todayKey = dateKey(now.getFullYear(), now.getMonth(), now.getDate())
  const selectedKey = dateKey(selectedYear, selectedMonth, selectedDay)

  const isToday = (d: number) => dateKey(year, month, d) === todayKey
  const isSelected = (d: number) => dateKey(year, month, d) === selectedKey

  // Events for selected day, sorted by time
  const dayEvents = events
    .filter(e => e.date === selectedKey)
    .sort((a, b) => a.time.localeCompare(b.time))

  // Days that have events — for dot indicators
  const daysWithEvents = new Set(events.filter(e => e.date.startsWith(`${year}-${String(month + 1).padStart(2, '0')}`)).map(e => parseInt(e.date.split('-')[2])))

  const handleSelectDay = (d: number) => {
    setSelectedDay(d)
    setSelectedMonth(month)
    setSelectedYear(year)
    setAdding(false)
  }

  const formatTimeDisplay = (t: string) => {
    const [h, m] = t.split(':').map(Number)
    const ampm = h >= 12 ? 'PM' : 'AM'
    const h12 = h % 12 || 12
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`
  }

  const handleAddEvent = () => {
    const title = newTitle.trim()
    if (!title) return
    const ev: CalEvent = {
      id: `${Date.now()}`,
      title,
      time: newTime,
      date: selectedKey,
      color: newColor,
    }
    setEvents(prev => [...prev, ev])
    setNewTitle('')
    setNewTime('09:00')
    setNewColor(EVENT_COLORS[0])
    setAdding(false)
  }

  const handleDeleteEvent = (id: string) => {
    setEvents(prev => prev.filter(e => e.id !== id))
  }

  const selectedLabel = new Date(selectedYear, selectedMonth, selectedDay)
    .toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric' })

  return (
    <div className="wp-calendar">
      <div className="wp-panel__header">
        <span className="wp-panel__title">Calendar</span>
      </div>

      {/* Mini calendar */}
      <div className="mini-cal">
        <div className="mini-cal__nav">
          <span className="mini-cal__month-label">{monthName} {year}</span>
          <div style={{ display: 'flex' }}>
            <button className="btn-icon mini-cal__nav-btn" onClick={() => setViewDate(new Date(year, month - 1, 1))} aria-label="Previous month">
              <ChevronLeft size={14} />
            </button>
            <button className="btn-icon mini-cal__nav-btn" onClick={() => setViewDate(new Date(year, month + 1, 1))} aria-label="Next month">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>

        <div className="mini-cal__grid">
          {DOW.map((d, i) => <span key={i} className="mini-cal__dow">{d}</span>)}
          {cells.map((d, i) => (
            <button
              key={i}
              className={[
                'mini-cal__day',
                !d ? 'mini-cal__day--empty' : '',
                d && isToday(d) ? 'mini-cal__day--today' : '',
                d && isSelected(d) && !isToday(d) ? 'mini-cal__day--selected' : '',
              ].join(' ')}
              onClick={() => d && handleSelectDay(d)}
              disabled={!d}
              tabIndex={d ? 0 : -1}
            >
              {d || ''}
              {d && daysWithEvents.has(d) && !isToday(d) && !isSelected(d) && (
                <span className="mini-cal__dot" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Selected day events */}
      <div className="wp-section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 12 }}>
        <span style={{ fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedLabel}</span>
      </div>

      <div className="wp-events">
        {dayEvents.length === 0 && !adding && (
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', padding: '4px 0 8px', textAlign: 'center' }}>
            No events
          </div>
        )}

        {dayEvents.map(ev => (
          <div key={ev.id} className="wp-event" style={{ position: 'relative' }} role="group">
            <div className="wp-event__dot" style={{ background: ev.color }} />
            <div className="wp-event__body">
              <div className="wp-event__title">{ev.title}</div>
              <div className="wp-event__time">{formatTimeDisplay(ev.time)}</div>
            </div>
            <button
              className="wp-event__delete"
              onClick={() => handleDeleteEvent(ev.id)}
              title="Remove event"
              aria-label="Delete event"
            >
              <X size={11} />
            </button>
          </div>
        ))}

        {/* Inline new event form */}
        {adding ? (
          <div className="wp-event-form">
            <input
              ref={titleRef}
              className="wp-input"
              placeholder="Event title"
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddEvent(); if (e.key === 'Escape') setAdding(false) }}
              style={{ marginBottom: 6 }}
            />
            <div style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <input
                type="time"
                className="wp-input"
                value={newTime}
                onChange={e => setNewTime(e.target.value)}
                style={{ flex: 1, fontSize: 12 }}
              />
              <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                {EVENT_COLORS.map(c => (
                  <button
                    key={c}
                    onClick={() => setNewColor(c)}
                    style={{
                      width: 14, height: 14, borderRadius: '50%', background: c, border: 'none',
                      cursor: 'pointer', outline: newColor === c ? `2px solid ${c}` : 'none',
                      outlineOffset: 1, flexShrink: 0,
                    }}
                    title={c}
                  />
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="wp-add-btn" style={{ flex: 1 }} onClick={handleAddEvent}>
                <Check size={12} /> Save
              </button>
              <button className="wp-add-btn" style={{ flex: 1 }} onClick={() => setAdding(false)}>
                <X size={12} /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <button className="wp-add-btn" onClick={() => setAdding(true)}>
            <Plus size={12} />
            New event
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Tasks Panel ──────────────────────────────────────────────────────────────

interface Task { id: string; text: string; done: boolean }

function TasksPanel() {
  const [tasks, setTasks] = useState<Task[]>(() => {
    try { return JSON.parse(localStorage.getItem('tmail_tasks') ?? '[]') } catch { return [] }
  })
  const [input, setInput] = useState('')

  useEffect(() => {
    try { localStorage.setItem('tmail_tasks', JSON.stringify(tasks)) } catch { /* ignore */ }
  }, [tasks])

  const addTask = () => {
    const text = input.trim()
    if (!text) return
    setTasks((t) => [...t, { id: `${Date.now()}`, text, done: false }])
    setInput('')
  }

  const toggleTask = (id: string) =>
    setTasks((t) => t.map((task) => task.id === id ? { ...task, done: !task.done } : task))

  const removeTask = (id: string) =>
    setTasks((t) => t.filter((task) => task.id !== id))

  const pending = tasks.filter((t) => !t.done)
  const done = tasks.filter((t) => t.done)

  return (
    <div className="wp-tasks">
      <div className="wp-panel__header">
        <span className="wp-panel__title">My Tasks</span>
      </div>

      <div className="wp-tasks__input-row">
        <input
          className="wp-input"
          placeholder="Add a task..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addTask()}
        />
        <button className="btn-icon" onClick={addTask} disabled={!input.trim()} title="Add task">
          <Plus size={15} />
        </button>
      </div>

      <div className="wp-tasks__list">
        {tasks.length === 0 && <div className="wp-empty">No tasks yet</div>}

        <AnimatePresence initial={false}>
          {pending.map((task) => (
            <motion.div
              key={task.id}
              className="wp-task"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.15 }}
            >
              <button className="wp-task__check" onClick={() => toggleTask(task.id)} aria-label="Complete task" />
              <span className="wp-task__text">{task.text}</span>
              <button className="wp-task__del btn-icon" onClick={() => removeTask(task.id)} aria-label="Remove task">
                <X size={11} />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>

        {done.length > 0 && (
          <div className="wp-tasks__done-label">Completed ({done.length})</div>
        )}

        <AnimatePresence initial={false}>
          {done.map((task) => (
            <motion.div
              key={task.id}
              className="wp-task wp-task--done"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.15 }}
            >
              <button className="wp-task__check wp-task__check--done" onClick={() => toggleTask(task.id)} aria-label="Undo complete">
                <Check size={10} />
              </button>
              <span className="wp-task__text">{task.text}</span>
              <button className="wp-task__del btn-icon" onClick={() => removeTask(task.id)} aria-label="Remove task">
                <X size={11} />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  )
}

// ─── Notes Panel ──────────────────────────────────────────────────────────────

interface Note { id: string; text: string; date: string }

function NotesPanel() {
  const [notes, setNotes] = useState<Note[]>(() => {
    try { return JSON.parse(localStorage.getItem('tmail_notes') ?? '[]') } catch { return [] }
  })
  const [input, setInput] = useState('')

  useEffect(() => {
    try { localStorage.setItem('tmail_notes', JSON.stringify(notes)) } catch { /* ignore */ }
  }, [notes])

  const addNote = () => {
    const text = input.trim()
    if (!text) return
    setNotes((n) => [{
      id: `${Date.now()}`,
      text,
      date: new Date().toLocaleDateString('default', { month: 'short', day: 'numeric' }),
    }, ...n])
    setInput('')
  }

  return (
    <div className="wp-notes">
      <div className="wp-panel__header">
        <span className="wp-panel__title">Keep</span>
      </div>

      <div className="wp-notes__new">
        <textarea
          className="wp-textarea"
          placeholder="Take a note..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          rows={3}
        />
        {input.trim() && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6, gap: 6 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setInput('')}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={addNote}>Save</button>
          </div>
        )}
      </div>

      <div className="wp-notes__list">
        {notes.length === 0 && <div className="wp-empty">Notes you add appear here</div>}
        <AnimatePresence initial={false}>
          {notes.map((note) => (
            <motion.div
              key={note.id}
              className="wp-note"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.15 }}
            >
              <div className="wp-note__text">{note.text}</div>
              <div className="wp-note__footer">
                <span className="wp-note__date">{note.date}</span>
                <button
                  className="btn-icon"
                  style={{ padding: '2px', opacity: 0.6 }}
                  onClick={() => setNotes((n) => n.filter((x) => x.id !== note.id))}
                  aria-label="Delete note"
                >
                  <X size={11} />
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  )
}

// ─── Contacts Panel ───────────────────────────────────────────────────────────

const CONTACTS = [
  { id: '1', name: 'Alice Chen',     email: 'alice@acme.com',    initials: 'AC', color: '#5B9FE8' },
  { id: '2', name: 'Bob Martinez',   email: 'bob@acme.com',      initials: 'BM', color: '#6DB87A' },
  { id: '3', name: 'Carol Wang',     email: 'carol@acme.com',    initials: 'CW', color: '#E07040' },
  { id: '4', name: 'David Kim',      email: 'david@acme.com',    initials: 'DK', color: '#F59E0B' },
  { id: '5', name: 'Emma Davis',     email: 'emma@acme.com',     initials: 'ED', color: '#A78BFA' },
  { id: '6', name: 'Frank Nguyen',   email: 'frank@acme.com',    initials: 'FN', color: '#E85454' },
]

function ContactsPanel() {
  const [query, setQuery] = useState('')

  const filtered = CONTACTS.filter((c) =>
    !query ||
    c.name.toLowerCase().includes(query.toLowerCase()) ||
    c.email.toLowerCase().includes(query.toLowerCase())
  )

  return (
    <div className="wp-contacts">
      <div className="wp-panel__header">
        <span className="wp-panel__title">Contacts</span>
        <button className="wp-panel__action">
          <Plus size={13} /> Create
        </button>
      </div>

      <div className="wp-contacts__search">
        <Search size={13} className="wp-contacts__search-icon" />
        <input
          className="wp-contacts__input"
          placeholder="Search contacts..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="wp-contacts__list">
        {filtered.length === 0 && <div className="wp-empty">No contacts found</div>}
        {filtered.map((c) => (
          <div key={c.id} className="wp-contact">
            <div className="wp-contact__avatar" style={{ background: c.color }}>
              {c.initials}
            </div>
            <div className="wp-contact__info">
              <div className="wp-contact__name">{c.name}</div>
              <div className="wp-contact__email">{c.email}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Chat Panel ───────────────────────────────────────────────────────────────

interface ChatMessage { id: string; from: 'me' | 'them'; text: string; time: string }
interface ChatConversation { id: string; name: string; email: string; lastMsg: string; time: string; unread: number; messages: ChatMessage[] }

const MOCK_CHATS: ChatConversation[] = [
  {
    id: 'c1', name: 'Jordan Lee', email: 'jordan@tmail.app',
    lastMsg: 'Sounds good, see you then!', time: '9:41 AM', unread: 2,
    messages: [
      { id: 'm1', from: 'them', text: 'Hey, did you get the updated proposal?', time: '9:30 AM' },
      { id: 'm2', from: 'me',   text: 'Yes just reviewed it — looks great!', time: '9:35 AM' },
      { id: 'm3', from: 'them', text: 'Sounds good, see you then!', time: '9:41 AM' },
    ],
  },
  {
    id: 'c2', name: 'Priya Sharma', email: 'priya@tmail.app',
    lastMsg: 'Can you share the report by EOD?', time: '8:15 AM', unread: 0,
    messages: [
      { id: 'm1', from: 'them', text: 'Good morning! Hope the launch went well.', time: '8:00 AM' },
      { id: 'm2', from: 'me',   text: 'It did! Numbers are looking good so far.', time: '8:10 AM' },
      { id: 'm3', from: 'them', text: 'Can you share the report by EOD?', time: '8:15 AM' },
    ],
  },
  {
    id: 'c3', name: 'CTO Mike', email: 'cto@tmail.app',
    lastMsg: "Let's schedule a call this week", time: 'Yesterday', unread: 1,
    messages: [
      { id: 'm1', from: 'them', text: 'Quick update on the backend migration?', time: 'Yesterday' },
      { id: 'm2', from: 'me',   text: 'Going smoothly — about 80% complete.', time: 'Yesterday' },
      { id: 'm3', from: 'them', text: "Let's schedule a call this week", time: 'Yesterday' },
    ],
  },
]

function ChatPanel() {
  const [activeChat, setActiveChat] = useState<string | null>(null)
  const [chats, setChats] = useState(MOCK_CHATS)
  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const conversation = chats.find((c) => c.id === activeChat)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [activeChat, conversation?.messages.length])

  const sendMessage = () => {
    if (!input.trim() || !activeChat) return
    const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    setChats((prev) => prev.map((c) => c.id === activeChat ? {
      ...c,
      lastMsg: input.trim(),
      time: now,
      messages: [...c.messages, { id: Date.now().toString(), from: 'me', text: input.trim(), time: now }],
    } : c))
    setInput('')
  }

  const COLORS = ['#E07040', '#5B9FE8', '#6DB87A', '#A855F7', '#F59E0B', '#E85454', '#06B6D4', '#EC4899']
  const avatarColor = (name: string) => {
    let h = 0
    for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h)
    return COLORS[Math.abs(h) % COLORS.length]
  }
  const initials = (name: string) => name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase()

  return (
    <div className="chat-panel">
      {!activeChat ? (
        <div className="chat-list">
          <div className="chat-list__header">
            <span>Messages</span>
            <button className="btn-icon" title="New chat"><Plus size={15} /></button>
          </div>
          {chats.map((c) => (
            <div
              key={c.id}
              className={`chat-list__row${c.unread > 0 ? ' chat-list__row--unread' : ''}`}
              onClick={() => { setActiveChat(c.id); setChats((p) => p.map((x) => x.id === c.id ? { ...x, unread: 0 } : x)) }}
            >
              <div className="chat-list__av" style={{ background: avatarColor(c.name) }}>{initials(c.name)}</div>
              <div className="chat-list__info">
                <div className="chat-list__name-row">
                  <span className="chat-list__name">{c.name}</span>
                  <span className="chat-list__time">{c.time}</span>
                </div>
                <div className="chat-list__preview-row">
                  <span className="chat-list__preview">{c.lastMsg}</span>
                  {c.unread > 0 && <span className="chat-list__badge">{c.unread}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="chat-convo">
          <div className="chat-convo__header">
            <button className="btn-icon" onClick={() => setActiveChat(null)}><ArrowLeft size={15} /></button>
            <div className="chat-convo__av" style={{ background: conversation ? avatarColor(conversation.name) : '#888' }}>
              {conversation ? initials(conversation.name) : '?'}
            </div>
            <div>
              <div className="chat-convo__name">{conversation?.name}</div>
              <div className="chat-convo__email">{conversation?.email}</div>
            </div>
          </div>
          <div className="chat-convo__messages">
            {conversation?.messages.map((msg) => (
              <div key={msg.id} className={`chat-msg chat-msg--${msg.from}`}>
                <div className="chat-msg__bubble">{msg.text}</div>
                <div className="chat-msg__time">{msg.time}</div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div className="chat-convo__input">
            <input
              className="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() } }}
              placeholder="Message…"
              autoFocus
            />
            <button
              className="chat-send-btn"
              onClick={sendMessage}
              disabled={!input.trim()}
              title="Send (Enter)"
            >
              <Send size={15} />
            </button>
          </div>
        </div>
      )}
      <style>{`
        .chat-panel { display: flex; flex-direction: column; height: 100%; }

        /* List */
        .chat-list { display: flex; flex-direction: column; height: 100%; }
        .chat-list__header { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px; font-size: 13px; font-weight: 600; color: var(--text-primary); border-bottom: 1px solid var(--border); flex-shrink: 0; }
        .chat-list__row { display: flex; align-items: center; gap: 10px; padding: 10px 14px; cursor: pointer; transition: background var(--duration-fast); border-bottom: 1px solid var(--border); }
        .chat-list__row:hover { background: var(--bg-hover); }
        .chat-list__row--unread .chat-list__name { font-weight: 700; color: var(--text-primary); }
        .chat-list__row--unread .chat-list__preview { font-weight: 500; color: var(--text-secondary); }
        .chat-list__av { width: 38px; height: 38px; min-width: 38px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; color: #fff; flex-shrink: 0; }
        .chat-list__info { flex: 1; min-width: 0; }
        .chat-list__name-row { display: flex; justify-content: space-between; align-items: center; gap: 6px; }
        .chat-list__name { font-size: 13px; font-weight: 500; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .chat-list__time { font-size: 11px; color: var(--text-tertiary); flex-shrink: 0; font-family: var(--font-mono); }
        .chat-list__preview-row { display: flex; align-items: center; justify-content: space-between; gap: 6px; margin-top: 2px; }
        .chat-list__preview { font-size: 12px; color: var(--text-tertiary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
        .chat-list__badge { min-width: 18px; height: 18px; border-radius: 9px; background: var(--brand); color: #fff; font-size: 10px; font-weight: 700; display: flex; align-items: center; justify-content: center; padding: 0 5px; flex-shrink: 0; }

        /* Conversation */
        .chat-convo { display: flex; flex-direction: column; height: 100%; }
        .chat-convo__header { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-bottom: 1px solid var(--border); flex-shrink: 0; }
        .chat-convo__av { width: 34px; height: 34px; min-width: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; color: #fff; }
        .chat-convo__name { font-size: 13px; font-weight: 600; color: var(--text-primary); }
        .chat-convo__email { font-size: 11px; color: var(--text-tertiary); }
        .chat-convo__messages { flex: 1; overflow-y: auto; padding: 12px; display: flex; flex-direction: column; gap: 8px; }
        .chat-msg { display: flex; flex-direction: column; }
        .chat-msg--me { align-items: flex-end; }
        .chat-msg--them { align-items: flex-start; }
        .chat-msg__bubble { max-width: 80%; padding: 8px 12px; border-radius: 16px; font-size: 13px; line-height: 1.45; }
        .chat-msg--me .chat-msg__bubble { background: var(--brand); color: #fff; border-bottom-right-radius: 4px; }
        .chat-msg--them .chat-msg__bubble { background: var(--bg-elevated); color: var(--text-primary); border: 1px solid var(--border); border-bottom-left-radius: 4px; }
        .chat-msg__time { font-size: 10px; color: var(--text-tertiary); margin-top: 3px; padding: 0 4px; }
        .chat-convo__input { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-top: 1px solid var(--border); flex-shrink: 0; }
        .chat-input { flex: 1; padding: 8px 12px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 20px; color: var(--text-primary); font-family: var(--font-ui); font-size: 13px; outline: none; }
        .chat-input:focus { border-color: var(--brand); }
        .chat-send-btn { width: 32px; height: 32px; border-radius: 50%; background: var(--brand); color: #fff; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: background var(--duration-fast); }
        .chat-send-btn:hover { background: var(--brand-hover); }
        .chat-send-btn:disabled { opacity: 0.4; cursor: not-allowed; }
      `}</style>
    </div>
  )
}

// ─── Panel map ────────────────────────────────────────────────────────────────

const PANEL_CONTENT: Record<WorkspacePanelType, React.ReactNode> = {
  calendar: <CalendarPanel />,
  tasks:    <TasksPanel />,
  notes:    <NotesPanel />,
  contacts: <ContactsPanel />,
  chat:     <ChatPanel />,
}

const WORKSPACE_ICONS: { id: WorkspacePanelType; icon: React.ElementType; title: string }[] = [
  { id: 'calendar', icon: CalendarDays,   title: 'Calendar' },
  { id: 'tasks',    icon: CheckSquare,    title: 'Tasks' },
  { id: 'notes',    icon: StickyNote,     title: 'Keep Notes' },
  { id: 'contacts', icon: Users,          title: 'Contacts' },
  { id: 'chat',     icon: MessageCircle,  title: 'Team Chat' },
]

// ─── WorkspaceSidebar ─────────────────────────────────────────────────────────

export function WorkspaceSidebar() {
  const { workspacePanel, setWorkspacePanel } = useUIStore()

  const toggle = (id: WorkspacePanelType) =>
    setWorkspacePanel(workspacePanel === id ? null : id)

  return (
    <div className="workspace-sidebar">
      {/* Sliding panel */}
      <AnimatePresence initial={false}>
        {workspacePanel && (
          <motion.div
            className="workspace-panel"
            key={workspacePanel}
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 300, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="workspace-panel__inner">
              {PANEL_CONTENT[workspacePanel]}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Icon strip */}
      <div className="workspace-icon-strip">
        {WORKSPACE_ICONS.map(({ id, icon: Icon, title }) => (
          <button
            key={id}
            className={`workspace-icon-btn${workspacePanel === id ? ' workspace-icon-btn--active' : ''}`}
            onClick={() => toggle(id)}
            title={title}
            aria-label={title}
          >
            <Icon size={20} />
          </button>
        ))}
      </div>

      <style>{`
        /* ─── Shell ── */
        .workspace-sidebar {
          display: flex;
          flex-direction: row;
          height: 100%;
          border-left: 1px solid var(--border);
          flex-shrink: 0;
        }

        .workspace-panel {
          overflow: hidden;
          border-right: 1px solid var(--border);
          background: var(--bg-panel);
          height: 100%;
          flex-shrink: 0;
        }

        .workspace-panel__inner {
          width: 300px;
          height: 100%;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
        }

        /* ─── Icon strip ── */
        .workspace-icon-strip {
          width: 52px;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 8px 0;
          gap: 2px;
          background: var(--bg-sidebar);
          flex-shrink: 0;
        }

        .workspace-icon-btn {
          width: 40px;
          height: 40px;
          border-radius: var(--radius-md);
          border: none;
          background: transparent;
          color: var(--text-tertiary);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background var(--duration-fast), color var(--duration-fast);
          position: relative;
        }

        .workspace-icon-btn:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }

        .workspace-icon-btn--active {
          color: var(--brand);
        }

        .workspace-icon-btn--active::after {
          content: '';
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 3px;
          height: 24px;
          background: var(--brand);
          border-radius: 3px 0 0 3px;
        }

        /* ─── Shared panel styles ── */
        .wp-panel__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 16px 12px;
          border-bottom: 1px solid var(--border);
          flex-shrink: 0;
        }

        .wp-panel__title {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .wp-panel__action {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          font-family: var(--font-ui);
          font-weight: 500;
          color: var(--brand);
          background: none;
          border: none;
          cursor: pointer;
          padding: 3px 6px;
          border-radius: var(--radius-sm);
        }
        .wp-panel__action:hover { background: var(--brand-subtle); }

        .wp-section-label {
          padding: 12px 16px 6px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--text-tertiary);
        }

        .wp-empty {
          padding: 24px 16px;
          text-align: center;
          font-size: 12px;
          color: var(--text-tertiary);
        }

        .wp-input {
          flex: 1;
          background: var(--bg-input);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          padding: 7px 10px;
          font-family: var(--font-ui);
          font-size: 13px;
          color: var(--text-primary);
          outline: none;
          min-width: 0;
          transition: border-color var(--duration-fast);
        }
        .wp-input::placeholder { color: var(--text-tertiary); }
        .wp-input:focus { border-color: var(--border-focus); }

        .wp-textarea {
          width: 100%;
          background: var(--bg-input);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          padding: 8px 10px;
          font-family: var(--font-ui);
          font-size: 13px;
          color: var(--text-primary);
          outline: none;
          resize: none;
          transition: border-color var(--duration-fast);
        }
        .wp-textarea::placeholder { color: var(--text-tertiary); }
        .wp-textarea:focus { border-color: var(--border-focus); }

        /* ─── Calendar ── */
        .wp-calendar {
          display: flex;
          flex-direction: column;
        }

        .mini-cal {
          padding: 12px 12px 8px;
          border-bottom: 1px solid var(--border);
        }

        .mini-cal__nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
        }

        .mini-cal__month-label {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .mini-cal__nav-btn {
          padding: 3px !important;
          width: 24px;
          height: 24px;
        }

        .mini-cal__grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 1px;
        }

        .mini-cal__dow {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 24px;
          font-size: 10px;
          font-weight: 600;
          color: var(--text-tertiary);
          text-transform: uppercase;
        }

        .mini-cal__day {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 28px;
          font-size: 12px;
          color: var(--text-secondary);
          border-radius: var(--radius-sm);
          border: none;
          background: transparent;
          cursor: pointer;
          transition: background var(--duration-fast), color var(--duration-fast);
        }

        .mini-cal__day:hover:not(:disabled):not(.mini-cal__day--today) {
          background: var(--bg-hover);
          color: var(--text-primary);
        }

        .mini-cal__day--empty {
          cursor: default;
          pointer-events: none;
        }

        .mini-cal__day--today {
          background: var(--brand);
          color: #fff;
          font-weight: 700;
          border-radius: 50%;
        }

        .mini-cal__day--selected {
          background: var(--bg-selected-strong);
          color: var(--text-primary);
          font-weight: 600;
          border-radius: 50%;
        }

        .wp-events {
          padding: 6px 12px 12px;
        }

        .wp-event {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 7px 0;
          border-bottom: 1px solid var(--border);
        }
        .wp-event:last-of-type { border-bottom: none; }

        .wp-event__dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          margin-top: 5px;
          flex-shrink: 0;
        }

        .wp-event__body { flex: 1; min-width: 0; }

        .wp-event__title {
          font-size: 13px;
          color: var(--text-primary);
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .wp-event__time {
          font-size: 11px;
          color: var(--text-tertiary);
          margin-top: 1px;
          font-family: var(--font-mono);
        }

        .wp-event__delete {
          display: none;
          align-items: center;
          justify-content: center;
          background: none;
          border: none;
          cursor: pointer;
          color: var(--text-tertiary);
          padding: 2px;
          border-radius: var(--radius-sm);
          flex-shrink: 0;
          margin-top: 2px;
        }
        .wp-event:hover .wp-event__delete { display: flex; }
        .wp-event__delete:hover { color: var(--red, #ef4444); background: var(--bg-hover); }

        .wp-event-form {
          margin-top: 8px;
          padding: 10px;
          background: var(--bg-elevated);
          border: 1px solid var(--border-strong);
          border-radius: var(--radius-md);
        }

        .mini-cal__dot {
          display: block;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: var(--brand);
          position: absolute;
          bottom: 1px;
          left: 50%;
          transform: translateX(-50%);
        }

        .wp-add-btn {
          display: flex;
          align-items: center;
          gap: 5px;
          margin-top: 8px;
          padding: 5px 8px;
          font-size: 12px;
          font-family: var(--font-ui);
          font-weight: 500;
          color: var(--brand);
          background: none;
          border: 1px dashed var(--border-strong);
          border-radius: var(--radius-md);
          cursor: pointer;
          width: 100%;
          justify-content: center;
          transition: background var(--duration-fast), border-color var(--duration-fast);
        }
        .wp-add-btn:hover {
          background: var(--brand-subtle);
          border-color: var(--brand);
        }

        /* ─── Tasks ── */
        .wp-tasks {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .wp-tasks__input-row {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 12px 12px 8px;
          border-bottom: 1px solid var(--border);
        }

        .wp-tasks__list {
          flex: 1;
          overflow-y: auto;
          padding: 6px 8px;
        }

        .wp-task {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 8px;
          border-radius: var(--radius-md);
          overflow: hidden;
        }
        .wp-task:hover { background: var(--bg-hover); }
        .wp-task:hover .wp-task__del { opacity: 1; }

        .wp-task__check {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 1.5px solid var(--border-strong);
          background: transparent;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: background var(--duration-fast), border-color var(--duration-fast);
          padding: 0;
        }
        .wp-task__check:hover { border-color: var(--brand); }

        .wp-task__check--done {
          background: var(--green);
          border-color: var(--green);
          color: white;
        }

        .wp-task__text {
          flex: 1;
          font-size: 13px;
          color: var(--text-primary);
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .wp-task--done .wp-task__text {
          text-decoration: line-through;
          color: var(--text-tertiary);
        }

        .wp-task__del {
          padding: 2px !important;
          opacity: 0;
          flex-shrink: 0;
          transition: opacity var(--duration-fast);
        }

        .wp-tasks__done-label {
          padding: 10px 8px 4px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--text-tertiary);
        }

        /* ─── Notes ── */
        .wp-notes {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .wp-notes__new {
          padding: 12px;
          border-bottom: 1px solid var(--border);
        }

        .wp-notes__list {
          flex: 1;
          overflow-y: auto;
          padding: 8px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .wp-note {
          background: var(--bg-elevated);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          padding: 10px 12px 8px;
          transition: box-shadow var(--duration-fast);
        }
        .wp-note:hover { box-shadow: var(--shadow-sm); }

        .wp-note__text {
          font-size: 13px;
          color: var(--text-primary);
          line-height: 1.5;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .wp-note__footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 6px;
        }

        .wp-note__date {
          font-size: 11px;
          color: var(--text-tertiary);
          font-family: var(--font-mono);
        }

        /* ─── Contacts ── */
        .wp-contacts {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .wp-contacts__search {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-bottom: 1px solid var(--border);
          position: relative;
        }

        .wp-contacts__search-icon {
          position: absolute;
          left: 22px;
          color: var(--text-tertiary);
          pointer-events: none;
        }

        .wp-contacts__input {
          width: 100%;
          background: var(--bg-input);
          border: 1px solid var(--border);
          border-radius: var(--radius-full);
          padding: 6px 12px 6px 28px;
          font-family: var(--font-ui);
          font-size: 12px;
          color: var(--text-primary);
          outline: none;
          transition: border-color var(--duration-fast);
        }
        .wp-contacts__input::placeholder { color: var(--text-tertiary); }
        .wp-contacts__input:focus { border-color: var(--border-focus); }

        .wp-contacts__list {
          flex: 1;
          overflow-y: auto;
          padding: 6px 8px;
        }

        .wp-contact {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 8px;
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: background var(--duration-fast);
        }
        .wp-contact:hover { background: var(--bg-hover); }

        .wp-contact__avatar {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 700;
          color: #fff;
          flex-shrink: 0;
        }

        .wp-contact__name {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-primary);
        }

        .wp-contact__email {
          font-size: 11px;
          color: var(--text-tertiary);
          margin-top: 1px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .wp-contact__info {
          min-width: 0;
          flex: 1;
        }
      `}</style>
    </div>
  )
}
